//src/pages/CheckoutResultPage.jsx
/**
 * CheckoutResultPage
 * ------------------------------------------------------------
 * Handles the return from Stripe Checkout / Payment links.
 *   • Reads ?session_id or ?invoice_id from the URL
 *   • Calls the correct Cloud Function for the available param
 *   • Does NOT auto-open Stripe pages
 *   • Forces a fresh Firestore/Context sync for instant UI refresh
 *   • Presents success / failure UI and explicit invoice/receipt actions
 *
 * Enterprise-grade considerations built-in:
 *   • Unmount-safe async logic       (ignore flag)
 *   • No surprise invoice pop-ups    (manual user action only)
 *   • Defensive render guards        (null amount / null invoiceUrl handling)
 *   • a11y: role="status"            (SR announce after load)
 *   • No unnecessary re-renders      (state kept minimal)
 * ------------------------------------------------------------
 */

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { useBillings } from "../contexts/BillingContext";

/**
 * Normalize the backend result into one canonical page contract.
 *
 * Why:
 * - session_id flows may return invoiceId from the backend even when the
 *   URL itself has no invoice_id param.
 * - invoice_id flows may come directly from the URL without a session_id.
 * - The page should not mix raw URL params and backend data after the
 *   callable returns.
 *
 * Enterprise rule:
 * - Resolve identifiers once at the boundary.
 * - Downstream render/logging code reads only normalized state.
 *
 * @param {object|null|undefined} apiResp
 * @param {object} params
 * @param {string|null} params.sessionId
 * @param {string|null} params.invoiceId
 * @return {object}
 */
const normalizeCheckoutResultData = (
  apiResp,
  { sessionId = null, invoiceId = null } = {},
) => {
  const raw = apiResp || {};

  const resolvedSessionId =
    raw.sessionId || sessionId || null;

  const resolvedInvoiceId =
    raw.invoiceId || invoiceId || null;

  const resolvedInvoiceStatus =
    raw.invoiceStatus || raw.paymentStatus || null;

  const resolvedPaymentStatus =
    raw.paymentStatus || raw.invoiceStatus || null;

  return {
    ...raw,
    sessionId: resolvedSessionId,
    invoiceId: resolvedInvoiceId,
    invoiceStatus: resolvedInvoiceStatus,
    paymentStatus: resolvedPaymentStatus,
    paymentIntentStatus: raw.paymentIntentStatus || null,
    invoiceUrl: raw.invoiceUrl || null,
    invoiceNumber: raw.invoiceNumber || null,
    customerId: raw.customerId || null,
    subscriptionId: raw.subscriptionId || null,
    subtotal: raw.subtotal ?? null,
    total: raw.total ?? null,
    amountDue: raw.amountDue ?? null,
    amountPaid: raw.amountPaid ?? null,
    amountRemaining: raw.amountRemaining ?? null,
    amountTotal: raw.amountTotal ?? null,
    appliedBalance: raw.appliedBalance ?? null,
    currency: raw.currency ?? null,
    created: raw.created ?? null,
  };
};

const CheckoutResultPage = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { syncStripeSubscriptionDetails } = useBillings();

  // Extract session_id from the query‑string
  const params     = new URLSearchParams(search);
  const sessionId  = params.get("session_id");
  const invoiceId  = params.get("invoice_id");   // ← NEW

  // Local page‑state
  const [state, setState] = useState({ loading: true });

useEffect(() => {
  // Neither param present → nothing we can show
  if (!sessionId && !invoiceId) {
    setState({
      loading: false,
      error: "Missing session_id or invoice_id.",
    });
    return;
  }

  let ignore = false; // flag to suppress setState after unmount

  (async () => {
    try {
      /* ────────── choose CF based on the param we have ───────── */
      let apiResp;

      if (sessionId) {
        const getDetails = httpsCallable(
          functions,
          "getCheckoutSessionDetails",
        );
        ({data: apiResp} = await getDetails({sessionId}));
      } else {
        const getInv = httpsCallable(functions, "getInvoiceDetails");
        ({data: apiResp} = await getInv({invoiceId}));

        /* ------------------------------------------------------
         * IMPORTANT
         * - Preserve the backend-provided invoice paymentStatus.
         * - Do NOT force "paid" here.
         *
         * Why:
         * - getInvoiceDetails already returns Stripe’s actual invoice
         *   status.
         * - The backend billing contract intentionally distinguishes
         *   settled terminal states like:
         *     • paid
         *     • void
         * - Overwriting that value here creates frontend/backend drift.
         * ---------------------------------------------------- */
      }

      /* --------------------------------------------------------
       * IMPORTANT
       * - Do NOT auto-open the hosted invoice in a new tab.
       *
       * Why:
       * - The latest pricing flow intentionally avoids surprise tab
       *   behavior after Stripe handoff.
       * - CheckoutResultPage already renders explicit receipt / invoice
       *   actions for the user.
       * - Auto-opening here is not tied to a direct user gesture and
       *   can be blocked by browsers or feel noisy in the UX.
       * ------------------------------------------------------ */
      const normalizedResult = normalizeCheckoutResultData(
        apiResp,
        {
          sessionId,
          invoiceId,
        },
      );

      if (normalizedResult?.invoiceUrl) {
        console.log(
          "[CheckoutResultPage] Invoice URL available for manual viewing:",
          {
            invoiceUrl: normalizedResult.invoiceUrl,
            invoiceId: normalizedResult.invoiceId || null,
            sessionId: normalizedResult.sessionId || null,
          },
        );
      }

      /* --------------------------------------------------------
       * Enterprise-grade post-checkout reconciliation
       *
       * IMPORTANT:
       * - Render success/failure from Stripe result data first
       * - Then attempt Firestore/org reconciliation using the
       *   direct customerId returned by the result API
       * - Do not fail the whole result page if the background sync
       *   cannot complete immediately
       * ------------------------------------------------------ */
      if (!ignore) {
        setState({ loading: false, data: normalizedResult });
      }

      if (normalizedResult?.customerId) {
        try {
          await syncStripeSubscriptionDetails({
            stripeCustomerIdOverride: normalizedResult.customerId,
          });
        } catch (syncErr) {
          console.error(
            "[CheckoutResultPage] post-checkout sync failed:",
            syncErr?.message || syncErr,
          );
        }
      } else {
        console.warn(
          "[CheckoutResultPage] Missing customerId in checkout " +
          "result response; skipping billing sync.",
          {
            sessionId: normalizedResult?.sessionId || null,
            invoiceId: normalizedResult?.invoiceId || null,
          },
        );
      }
    } catch (err) {
      console.error(
        "[CheckoutResultPage] fetch failed:",
        err?.message || err,
      );
      if (!ignore) {
        setState({
          loading: false,
          error: "Unable to retrieve payment details.",
        });
      }
    }
  })();

  // Cleanup to avoid “setState on unmounted component” warnings
  return () => {
    ignore = true;
  };
}, [sessionId, invoiceId, syncStripeSubscriptionDetails]);

  /* ──────────────────────────────────────────
   * Status normalization / diagnostics
   * IMPORTANT:
   * - All hooks must be declared before any conditional return.
   * - Keep these derived values above the render-state guards so
   *   CheckoutResultPage stays aligned with React hook rules.
   * ────────────────────────────────────────── */

  const amountTotal = state.data?.amountTotal ?? null;
  const amountPaid = state.data?.amountPaid ?? amountTotal ?? null;
  const amountDue = state.data?.amountDue ?? null;
  const amountRemaining = state.data?.amountRemaining ?? null;
  const invoiceTotal = state.data?.total ?? null;
  const invoiceSubtotal = state.data?.subtotal ?? null;
  const currency = state.data?.currency ?? null;

  const appliedBalance =
    Number.isFinite(Number(state.data?.appliedBalance)) ?
      Number(state.data.appliedBalance) :
      (
        Number.isFinite(Number(invoiceTotal)) &&
        Number.isFinite(Number(amountPaid)) &&
        Number.isFinite(Number(amountRemaining)) ?
          Math.max(
              0,
              Number(invoiceTotal) -
              Number(amountPaid) -
              Number(amountRemaining),
          ) :
          null
      );

  /* --------------------------------------------------------
   * Normalize status across:
   *   • getCheckoutSessionDetails()
   *   • getInvoiceDetails()
   *
   * Why:
   * - Checkout-session results may return paymentStatus
   * - Invoice-details results may return invoiceStatus and/or
   *   paymentIntentStatus depending on the latest backend contract
   * - CheckoutResultPage must render consistently for BOTH
   *   ?session_id and ?invoice_id flows
   *
   * IMPORTANT
   * - Keep invoice-status-first behavior for invoice-driven returns.
   * - Recognize successful terminal states that the backend can
   *   intentionally return:
   *     • paid
   *     • void
   *     • no_payment_required
   * ------------------------------------------------------ */
  const invoiceStatus =
    state.data?.invoiceStatus ||
    state.data?.paymentStatus ||
    null;

  const paymentIntentStatus =
    state.data?.paymentIntentStatus || null;

  const effectivePaymentStatus =
    invoiceStatus ||
    paymentIntentStatus ||
    "unknown";

  const isSuccessfulTerminalResult =
    effectivePaymentStatus === "paid" ||
    effectivePaymentStatus === "void" ||
    effectivePaymentStatus === "no_payment_required";

  const successHeadline =
    effectivePaymentStatus === "paid"
      ? "Payment successful!"
      : "Billing change completed";

  const successSubline =
    effectivePaymentStatus === "paid"
      ? "Invoice paid"
      : effectivePaymentStatus === "void"
        ? "No payment was due"
        : effectivePaymentStatus === "no_payment_required"
          ? "No payment was required"
          : "Billing updated";

  const invoiceActionLabel =
    effectivePaymentStatus === "paid"
      ? "View receipt"
      : "View invoice";

  const primaryAmountLabel =
    effectivePaymentStatus === "paid" ?
      "Amount paid today" :
      effectivePaymentStatus === "void" ?
        "Amount due today" :
        effectivePaymentStatus === "no_payment_required" ?
          "Amount due today" :
          "Amount";

  /* --------------------------------------------------------
   * Normalize the primary amount shown in the success card.
   *
   * Why:
   * - The label and value must describe the same thing.
   * - "paid" should show amountPaid.
   * - "void" / "no_payment_required" should show amountDue.
   * - Fallback remains defensive for unexpected states.
   * ------------------------------------------------------ */
  const primaryAmountValue =
    effectivePaymentStatus === "paid" ?
      amountPaid :
      effectivePaymentStatus === "void" ||
      effectivePaymentStatus === "no_payment_required" ?
        amountDue :
        amountPaid;

  const showInvoiceTotal =
    Number.isFinite(Number(invoiceTotal)) &&
    (
      !Number.isFinite(Number(primaryAmountValue)) ||
      Math.abs(Number(invoiceTotal) - Number(primaryAmountValue)) >= 0.01
    );

  const showAppliedBalance =
    Number.isFinite(Number(appliedBalance)) &&
    Number(appliedBalance) >= 0.01;

  const zeroChargeCoveredByBalance =
    effectivePaymentStatus === "paid" &&
    Number.isFinite(Number(amountPaid)) &&
    Number(amountPaid) === 0 &&
    showAppliedBalance;

  const resolvedSessionId =
    state.data?.sessionId || sessionId || null;

  const resolvedInvoiceId =
    state.data?.invoiceId || invoiceId || null;

  useEffect(() => {
    if (state.loading || state.error || !state.data) {
      return;
    }

    console.log("[CheckoutResultPage] Normalized checkout result state:", {
      sessionId: resolvedSessionId,
      invoiceId: resolvedInvoiceId,
      invoiceStatus,
      paymentIntentStatus,
      effectivePaymentStatus,
      isSuccessfulTerminalResult,
      amountTotal,
      amountPaid,
      amountDue,
      amountRemaining,
      invoiceSubtotal,
      invoiceTotal,
      appliedBalance,
      currency,
      invoiceUrl: state.data?.invoiceUrl || null,
      customerId: state.data?.customerId || null,
      subscriptionId: state.data?.subscriptionId || null,
      invoiceNumber: state.data?.invoiceNumber || null,
    });
  }, [
    state.loading,
    state.error,
    state.data,
    resolvedSessionId,
    resolvedInvoiceId,
    invoiceStatus,
    paymentIntentStatus,
    effectivePaymentStatus,
    isSuccessfulTerminalResult,
    amountTotal,
    amountPaid,
    amountDue,
    amountRemaining,
    invoiceSubtotal,
    invoiceTotal,
    appliedBalance,
    currency,
  ]);

  /* ──────────────────────────────────────────
   *           RENDER STATES
   * ────────────────────────────────────────── */

  if (state.loading) {
    return (
      <Box sx={styles.center}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Finalising payment…</Typography>
      </Box>
    );
  }

  if (state.error) {
    return (
      <Box sx={styles.center} role="status">
        <Alert severity="error" sx={{ mb: 2 }}>
          {state.error}
        </Alert>
        <Button
          variant="outlined"
          onClick={() =>
            navigate("/settings/billing-settings", {
              state: { from: "checkout-result" },
            })
          }
        >
          Back to Billing
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={styles.center} role="status">
      {isSuccessfulTerminalResult ? (
        /* ───────────────────────── SUCCESS ───────────────────────── */
        <>
          {/* ───────── Invoice summary card ───────── */}
          <Card
            variant="outlined"
            sx={{
              width: "100%",
              maxWidth: 420,
              mx: "auto",
              pt: 4,
              px: 4,
              pb: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <CardContent
              sx={{
                p: 0,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {/* ── success badge ── */}
              <CheckCircleOutlineIcon
                sx={{ fontSize: 48, color: "success.main", mb: 1 }}
              />
              <Typography variant="h6" sx={{ mb: 1 }}>
                {successHeadline}
              </Typography>

              {/* first line ─ success state summary */}
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                {successSubline}
              </Typography>

              {/* primary settlement amount */}
              {primaryAmountValue != null && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary", mt: 1 }}
                  >
                    {primaryAmountLabel}
                  </Typography>

                  <Typography
                    variant="h4"
                    component="p"
                    sx={{ fontWeight: 700 }}
                  >
                    {Number(primaryAmountValue).toLocaleString(undefined, {
                      style: "currency",
                      currency: (currency || "usd").toUpperCase(),
                    })}
                  </Typography>
                </>
              )}

              {showInvoiceTotal && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Invoice total:{" "}
                  {Number(invoiceTotal).toLocaleString(undefined, {
                    style: "currency",
                    currency: (currency || "usd").toUpperCase(),
                  })}
                </Typography>
              )}

              {showAppliedBalance && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Applied balance:{" "}
                  {(-Number(appliedBalance)).toLocaleString(undefined, {
                    style: "currency",
                    currency: (currency || "usd").toUpperCase(),
                  })}
                </Typography>
              )}

              {zeroChargeCoveredByBalance && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, maxWidth: 320 }}
                >
                  No payment was charged today because available credits or
                  customer balance fully covered the invoice.
                </Typography>
              )}

              {/* link to Stripe */}
              {state.data?.invoiceUrl ? (
                <Typography
                  variant="subtitle2"
                  component="a"
                  href={state.data.invoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    mt: 0.5,
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    "&:hover": {
                      color: "text.primary",
                      textDecoration: "underline",
                    },
                  }}
                >
                  View invoice and payment details in Stripe&nbsp;
                  <ChevronRightIcon fontSize="inherit" />
                </Typography>
              ) : null}

              {/* invoice number row */}
              {state.data?.invoiceNumber && (
                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontWeight: 500 }}
                  >
                    Invoice number
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {state.data.invoiceNumber}
                  </Typography>
                </Box>
              )}

              {/* payment date row */}
              {state.data?.created && (
                <Box
                  sx={{
                    mt: 0.5,
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", fontWeight: 500 }}
                  >
                    Payment date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {new Date(state.data.created * 1000).toLocaleDateString(
                      undefined,
                      { year: "numeric", month: "long", day: "numeric" }
                    )}
                  </Typography>
                </Box>
              )}

              {/* ── button pair ── */}
              <Box
                sx={{
                  mt: 4,
                  display: "flex",
                  gap: 1,
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() =>
                    navigate("/settings/billing-settings", {
                      state: { from: "checkout-result" },
                    })
                  }
                  sx={{ flex: "0 0 160px" }}
                >
                  Back to Billing
                </Button>

                {state.data?.invoiceUrl ? (
                  <Button
                    variant="contained"
                    color="primary"
                    component="a"
                    href={state.data.invoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ flex: "0 0 160px" }}
                  >
                    {invoiceActionLabel}
                  </Button>
                ) : null}
              </Box>
            </CardContent>
          </Card>
        </>
      ) : (
        /* ───────────────────────── ERROR / UNPAID ───────────────────────── */
        <>
          <Card
            variant="outlined"
            sx={{
              width: "100%",
              maxWidth: 420,
              mx: "auto",
              pt: 4,
              px: 4,
              pb: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <CardContent
              sx={{
                p: 0,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {/* ── error badge ── */}
              <ErrorOutlineIcon
                sx={{ fontSize: 48, color: "error.main", mb: 1 }}
              />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Payment didn’t go through
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                Stripe returned status&nbsp;
                <strong>{effectivePaymentStatus}</strong>.
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 4, maxWidth: 300 }}
              >
                Update your card or retry the payment from the Billing page.
              </Typography>

              <Button
                variant="outlined"
                color="primary"
                onClick={() =>
                  navigate("/settings/billing-settings", {
                    state: { from: "checkout-result" },
                  })
                }
                sx={{ flex: "0 0 160px" }}
              >
                Back to Billing
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

/* Simple layout helper */
const styles = {
  center: {
    maxWidth: 500,
    m: "0 auto",
    pt: 4,
    textAlign: "center",
  },
};

export default CheckoutResultPage;
